import { useState } from 'react';
import { Target, Crosshair, Globe, TrendingDown, BarChart3 } from 'lucide-react';
import AttackPathGraph from './AttackPathGraph';
import KillChainGraph from './KillChainGraph';
import AssetGraph from './AssetGraph';
import RiskPropagationGraph from './RiskPropagationGraph';
import GraphMetrics from './GraphMetrics';

const TABS = [
  { id: 'attack', label: 'Attack Paths', icon: Target, description: 'Rutas de ataque desde el punto de entrada hasta el impacto' },
  { id: 'killchain', label: 'Kill Chain', icon: Crosshair, description: 'Cobertura MITRE ATT&CK — gaps en defensa' },
  { id: 'assets', label: 'Superficie', icon: Globe, description: 'Mapa de activos descubiertos y sus relaciones' },
  { id: 'risk', label: 'Riesgo', icon: TrendingDown, description: 'Propagacion de riesgo — camino de menor resistencia' },
  { id: 'metrics', label: 'Metricas', icon: BarChart3, description: 'Indicadores del grafo de ataque' },
];

interface GraphViewerProps {
  findings: any[];
  attackChains: any[];
  target: string;
}

export default function GraphViewer({ findings, attackChains, target }: GraphViewerProps): JSX.Element | null {
  const [activeTab, setActiveTab] = useState('attack');

  const actionable = findings.filter(f => f.severity !== 'info');
  if (actionable.length === 0) return null;

  return (
    <div className="rounded-2xl overflow-hidden border border-gray-700 print:hidden" style={{ background: '#0f172a' }}>
      {/* Tab bar */}
      <div className="flex items-center gap-1 px-3 py-2 border-b border-gray-700 overflow-x-auto">
        {TABS.map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium transition-all whitespace-nowrap ${
                isActive
                  ? 'bg-purple-600 text-white shadow-lg shadow-purple-500/25'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              <tab.icon className="h-3.5 w-3.5" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Description */}
      <div className="px-4 py-2 border-b border-gray-800">
        <p className="text-[10px] text-gray-500">
          {TABS.find(t => t.id === activeTab)?.description}
        </p>
      </div>

      {/* Active graph */}
      <div style={{ minHeight: '500px' }}>
        {activeTab === 'attack' && <AttackPathGraph findings={findings} attackChains={attackChains} target={target} />}
        {activeTab === 'killchain' && <KillChainGraph findings={findings} target={target} />}
        {activeTab === 'assets' && <AssetGraph findings={findings} target={target} />}
        {activeTab === 'risk' && <RiskPropagationGraph findings={findings} attackChains={attackChains} target={target} />}
        {activeTab === 'metrics' && <GraphMetrics findings={findings} attackChains={attackChains} />}
      </div>
    </div>
  );
}
